/**
 * ConversationArtifactsSection — the artifacts of a conversation as summary rows (no content), each linking to its
 * detail page. Module-specific previews are resolved through clientModuleRegistry. Demo page: /chats (the shared files
 * list of the context column); rows are the catalogue `ItemList`.
 */
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import React, { Suspense, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../components/ui/Button.js';
import { sentenceCase } from '../../../components/ui/FilterSelect.js';
import { ItemList, type ItemListItem } from '../../../components/ui/ItemList.js';
import { Skeleton } from '../../../components/ui/Skeleton.js';
import { listConversationArtifactsOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import type { ArtifactSummary } from '../../../generated/api/types.gen.js';
import { type ClientArtifactPreviewContribution, clientModuleRegistry } from '../../../modules/registry.js';
import { useEnabledModules } from '../../../modules/useEnabledModules.js';
import { relativeTime } from '../../../utils/relative-time.js';

const KIND_LABEL: Record<ArtifactSummary['kind'], string> = {
  decision: 'Decision',
  plan: 'Plan',
  draft: 'Draft',
  report: 'Report',
  code: 'Code',
  spec: 'Spec',
  design: 'Design',
  custom: 'Custom',
};

function getCustomType(metadata: ArtifactSummary['metadata']): string | null {
  const custom = metadata?.custom;
  if (!custom || typeof custom !== 'object' || Array.isArray(custom)) return null;
  const directType = custom['type'];
  if (typeof directType === 'string') return directType;
  const nestedCustom = custom['custom'];
  if (!nestedCustom || typeof nestedCustom !== 'object' || Array.isArray(nestedCustom)) return null;
  const nestedType = (nestedCustom as Record<string, unknown>)['type'];
  return typeof nestedType === 'string' ? nestedType : null;
}

/** One opened module preview, loaded on demand. */
function ArtifactPreview({ row, lazy }: { row: ArtifactSummary; lazy: ClientArtifactPreviewContribution['lazy'] }) {
  const Preview = useMemo(() => React.lazy(lazy), [lazy]);
  return (
    <Suspense
      fallback={
        <div className="space-y-1.5 py-1" aria-hidden="true">
          <Skeleton width="60%" height={12} />
          <Skeleton width="45%" height={12} />
        </div>
      }
    >
      <Preview artifactId={row.id} artifact={row} />
    </Suspense>
  );
}

interface ConversationArtifactsSectionProps {
  conversationId: string;
}

export function ConversationArtifactsSection({ conversationId }: ConversationArtifactsSectionProps) {
  const [openPreviews, setOpenPreviews] = useState<ReadonlySet<string>>(new Set());
  const { data: enabledModules = [] } = useEnabledModules();
  const previewContributions = useMemo(() => {
    const enabledIds = new Set(enabledModules.map((module) => module.id));
    return clientModuleRegistry.filter(enabledIds).flatMap((module) => module.artifactPreviews ?? []);
  }, [enabledModules]);

  const { data, isLoading, isError } = useQuery(
    listConversationArtifactsOptions({ path: { id: conversationId }, query: { view: 'summary' } }),
  );

  // The summary view answers with the summary list; the generated type is the union with the full list.
  const rows = (data?.data ?? []) as ArtifactSummary[];

  if (isLoading) {
    return (
      <div className="space-y-2" aria-hidden="true">
        <Skeleton width="70%" height={14} />
        <Skeleton width="55%" height={14} />
      </div>
    );
  }
  if (isError) return <p className="text-sm text-danger">Could not load artifacts.</p>;
  if (rows.length === 0) return <p className="text-sm text-muted">No artifacts yet.</p>;

  const togglePreview = (id: string) =>
    setOpenPreviews((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const items: ItemListItem[] = rows.map((row) => {
    const contribution = previewContributions.find((candidate) => candidate.matches(row)) ?? null;
    const customType = getCustomType(row.metadata);
    const open = openPreviews.has(row.id);
    return {
      id: row.id,
      leading: (
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent-muted text-accent-text">
          <FileText size={16} aria-hidden />
        </span>
      ),
      title: (
        <Link to={`/artifacts/${row.id}`} className="hover:text-accent-text">
          {row.title}
        </Link>
      ),
      subtitle: [KIND_LABEL[row.kind], customType && sentenceCase(customType), relativeTime(row.updatedAt)]
        .filter(Boolean)
        .join(' · '),
      value: contribution && (
        <Button variant="ghost" size="sm" onClick={() => togglePreview(row.id)}>
          {open ? `Hide ${(contribution.label ?? 'preview').toLowerCase()}` : (contribution.label ?? 'Preview')}
        </Button>
      ),
    };
  });

  return (
    <div className="space-y-3">
      <ItemList items={items} aria-label="Artifacts of this conversation" />
      {rows.map((row) => {
        const contribution = previewContributions.find((candidate) => candidate.matches(row)) ?? null;
        return contribution && openPreviews.has(row.id) ? (
          <ArtifactPreview key={row.id} row={row} lazy={contribution.lazy} />
        ) : null;
      })}
    </div>
  );
}
